import React, { useEffect, useState } from 'react';
import { COLORS } from '../../utils/constants';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { 
  LayoutDashboard, 
  TrendingUp, 
  Wallet, 
  Users, 
  Shield,
  CreditCard,
  ArrowDownUp,
  Rocket,
  X
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/constants';

const Sidebar = ({ isOpen, onClose, collapsed = false }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [
    {
      title: t('dashboard'),
      icon: LayoutDashboard,
      path: '/',
      roles: [USER_ROLES.USER, USER_ROLES.MANAGER, USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN],
    },
    {
      title: t('trading'),
      icon: TrendingUp,
      path: '/trading',
      roles: [USER_ROLES.USER, USER_ROLES.MANAGER, USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN],
    },
    {
      title: t('idoLaunchpad'),
      icon: Rocket,
      path: '/ido',
      roles: [USER_ROLES.USER, USER_ROLES.MANAGER, USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN],
    },
    {
      title: t('wallet'),
      icon: ArrowDownUp,
      path: '/wallet',
      roles: [USER_ROLES.USER, USER_ROLES.MANAGER, USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN],
    },
    {
      title: t('portfolio'),
      icon: Wallet,
      path: '/profile',
      roles: [USER_ROLES.USER, USER_ROLES.MANAGER, USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN],
    },
    {
      title: t('adminPanel'),
      icon: Users,
      path: '/admin-panel',
      roles: [USER_ROLES.MANAGER, USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN],
    },
    {
      title: t('security'),
      icon: Shield,
      path: '/security',
      roles: [USER_ROLES.USER, USER_ROLES.MANAGER, USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN],
    },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    !item.roles || item.roles.includes(user?.role)
  );

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Hide sidebar on mobile when on trading page
  const isTradingPage = location.pathname.startsWith('/trading');
  if (isMobile && isTradingPage && !isOpen) {
    return null;
  }

  // Mobile sidebar (overlay)
  if (isMobile) {
    if (!isOpen) return null;
    
    return (
      <>
        {/* Overlay */}
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 999,
          }}
        />

        {/* Sidebar */}
        <aside
          style={{
            width: '280px',
            backgroundColor: COLORS.card,
            height: '100vh',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 1000,
            padding: '0',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '4px 0 20px rgba(0,0,0,0.3)',
          }}
        >
          {/* Mobile Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: `1px solid ${COLORS.border}`,
          }}>
            <span style={{ fontSize: '20px', fontWeight: 'bold', color: COLORS.primary }}>
              ExDex
            </span>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: COLORS.textSecondary,
                cursor: 'pointer',
                padding: '8px',
              }}
            >
              <X size={24} />
            </button>
          </div>

          <nav style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    padding: '14px 24px',
                    color: active ? COLORS.primary : COLORS.textSecondary,
                    textDecoration: 'none',
                    fontSize: '15px',
                    fontWeight: active ? '600' : '400',
                    backgroundColor: active ? `${COLORS.primary}15` : 'transparent',
                    borderLeft: active ? `3px solid ${COLORS.primary}` : '3px solid transparent',
                  }}
                >
                  <Icon size={22} />
                  {item.title}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: `1px solid ${COLORS.border}`,
              color: COLORS.textSecondary,
              fontSize: '12px',
            }}
          >
            <div>ExDex v1.0.0</div>
            <div style={{ marginTop: '4px', opacity: 0.7 }}>© 2025 All rights reserved</div>
          </div>
        </aside>
      </>
    );
  }

  // Desktop sidebar
  return (
    <aside
      style={{
        width: collapsed ? '72px' : '240px',
        backgroundColor: COLORS.card,
        borderRight: `1px solid ${COLORS.border}`,
        height: 'calc(100vh - 64px)',
        position: 'fixed',
        top: '64px',
        left: 0,
        padding: collapsed ? '24px 0' : '24px 0',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        transition: 'width 0.3s ease',
        zIndex: 100,
        overflowX: 'hidden',
      }}
    >
      <nav style={{ flex: 1, overflowY: 'auto' }}>
        {filteredMenuItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              title={collapsed ? item.title : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: collapsed ? '14px 0' : '12px 24px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                color: active ? COLORS.primary : COLORS.textSecondary,
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: active ? '600' : '400',
                backgroundColor: active ? `${COLORS.primary}15` : 'transparent',
                borderLeft: collapsed ? 'none' : (active ? `3px solid ${COLORS.primary}` : '3px solid transparent'),
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.backgroundColor = COLORS.background;
                  e.currentTarget.style.color = COLORS.text;
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = COLORS.textSecondary;
                }
              }}
            >
              <Icon size={20} />
              {!collapsed && <span>{item.title}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div
          style={{
            padding: '16px 24px',
            borderTop: `1px solid ${COLORS.border}`,
            color: COLORS.textSecondary,
            fontSize: '12px',
          }}
        >
          <div>ExDex v1.0.0</div>
          <div style={{ marginTop: '4px', opacity: 0.7 }}>© 2025 All rights reserved</div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
